/**
 * cloneItemNode — SYNCHRONOUS DOM clone of a grid item's on-canvas visuals.
 *
 * Extracted from MoveDragGhost's clone effect so the native cross-tab drag
 * can reuse it: `DataTransfer.setDragImage()` must be called synchronously
 * INSIDE the `dragstart` handler with a node already in the document — a
 * React-effect clone arrives a tick too late. The ghost keeps consuming it
 * through its own host div; the drag path wraps it via `prepareDragImage`.
 *
 * The clone is the item chrome's CONTENT div (`[data-grid-item] > div`, the
 * white card with the real widget DOM) — pixel-identical to what the user
 * grabbed by construction (re-rendering through the preview pipeline drifted
 * fonts; see MoveDragGhost header).
 */

export interface ItemClone {
  node: HTMLElement;
  /** Layout size of the source content (CSS px, pre-canvas-zoom). */
  w: number;
  h: number;
}

/** Escape for a CSS attribute selector (CSS.escape is absent in jsdom). */
function escapeAttr(s: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(s);
  }
  return s.replace(/["\\]/g, "\\$&");
}

/** Clone the item's content div, inert (no caret, no duplicate ids). Null
 *  when the item isn't in the DOM. */
export function cloneItemNode(itemId: string): ItemClone | null {
  if (typeof document === "undefined") return null;
  const box = document.querySelector(
    `[data-grid-item="${escapeAttr(itemId)}"]`,
  );
  const content = box?.firstElementChild;
  if (!(content instanceof HTMLElement)) return null;
  const clone = content.cloneNode(true) as HTMLElement;
  clone.style.width = "100%";
  clone.style.height = "100%";
  clone.style.pointerEvents = "none";
  for (const el of clone.querySelectorAll("[contenteditable]"))
    el.setAttribute("contenteditable", "false");
  for (const el of clone.querySelectorAll("[id]")) el.removeAttribute("id");
  return {
    node: clone,
    w: content.offsetWidth || 1,
    h: content.offsetHeight || 1,
  };
}

export interface DragImage {
  el: HTMLElement;
  w: number;
  h: number;
  dispose: () => void;
}

/** Build an offscreen, document-attached drag image for `setDragImage`.
 *  Caller passes it to `setDragImage(img.el, dx, dy)` synchronously in
 *  `dragstart` and calls `dispose()` on `dragend` (plus a timer fallback —
 *  the browser snapshots the bitmap at dragstart, so early removal is safe).
 */
export function prepareDragImage(itemId: string): DragImage | null {
  const clone = cloneItemNode(itemId);
  if (!clone) return null;
  const wrap = document.createElement("div");
  wrap.style.position = "fixed";
  wrap.style.top = "-10000px";
  wrap.style.left = "-10000px";
  wrap.style.width = `${clone.w}px`;
  wrap.style.height = `${clone.h}px`;
  wrap.style.overflow = "hidden";
  wrap.style.background = "#fff";
  wrap.style.borderRadius = "6px";
  wrap.style.pointerEvents = "none";
  wrap.appendChild(clone.node);
  document.body.appendChild(wrap);
  let disposed = false;
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    wrap.remove();
  };
  return { el: wrap, w: clone.w, h: clone.h, dispose };
}
